/**
 * O critério de "este arraste move o quadro em vez de selecionar".
 *
 * ─── por que este arquivo existe separado ───────────────────────────────────────────────────
 *
 * Duas razões, e a segunda é a que decide.
 *
 * 1. `default-tool.ts` é arquivo do upstream, e tudo que se escreve lá vira patch a manter a
 *    cada atualização do Affine (Regra 8.15 — *arquivo novo não conflita; arquivo editado
 *    conflita*). O que sobra lá é a fiação; a decisão mora aqui.
 *
 * 2. **Este arquivo não importa NADA.** Sem `@blocksuite`, sem tipo do DOM, sem zod. É isso
 *    que permite `cli/tests/js/pan-no-vazio.test.ts` executá-lo de verdade nesta estação, sem
 *    `node_modules` do Affine e sem os ~16 minutos de build que a Regra 8.7.3 cataloga — o
 *    mesmo mecanismo que o avaliador de fórmula comprou.
 *
 *    ⚠️ No dia em que precisar importar de `@blocksuite`, ele deixou de ser puro e o teste
 *    para de carregar. Isso é o gate, não um acidente.
 *
 * ─── o que ele decide, e por que cada termo está aqui ───────────────────────────────────────
 *
 * A pergunta chega DEPOIS de o `DefaultTool` já ter concluído que não há nada sob o cursor —
 * a definição de "vazio" é a do upstream (`handleElementSelection` devolvendo falso), não uma
 * paralela nossa. Aqui só se decide o que fazer com o vazio.
 *
 * ─── por que são DUAS perguntas, e não uma ──────────────────────────────────────────────────
 *
 * O card 1850942686123525322 trouxe o mouse, e para ele UMA pergunta basta: no instante em que
 * o arraste começa já se sabe tudo (que ponteiro é, se o Shift está descido, se o ajuste está
 * ligado). O card 1864291811674032036 trouxe o dedo, e aí a resposta depende de algo que **no
 * início do arraste ainda não aconteceu**: quanto tempo o dedo ficou parado antes de andar.
 *
 * O detalhe que obriga a segunda pergunta está no framework: o arraste do Affine começa com
 * **2 pixels** de movimento (`isFarEnough`, `framework/std/src/event/utils.ts:6` — `d² > 4`).
 * Dois pixels é menos que o vagar de um dedo pousado numa tela capacitiva: o centroide do
 * contato se desloca enquanto a pressão se acomoda. Decidir "foi rápido ou foi devagar?" aos
 * 2px entregaria o gesto ao TREMOR — que cruzaria o limiar cedo, seria lido como "movimento
 * imediato", e panejaria o quadro exatamente quando o operador queria selecionar.
 *
 * Por isso, no dedo, a decisão **espera o dedo sair da folga** (`FOLGA_DO_DEDO`) e só então
 * pergunta o tempo. Enquanto ele está dentro dela, a resposta é `esperar-o-dedo` e **nada
 * acontece** — nem pan, nem caixa de seleção. Essa é a metade que torna o gesto imune ao
 * tremor, e é o que o operador descreveu por *"sem muito intervalo de tempo entre ter o touch
 * e movimentar"*: "movimentar" é ANDAR, não tremer.
 */

/** Os três valores que um `PointerEvent.pointerType` assume — mais o que navegador nenhum promete. */
export type TipoDePonteiro = 'mouse' | 'pen' | 'touch' | (string & {});

/**
 * O que fazer com um arraste começado em espaço vazio.
 *
 * `esperar-o-dedo` é um terceiro estado de verdade, não um "não sei" disfarçado de não: ele
 * significa *"o gesto ainda não disse o que é, e até dizer NADA acontece"* (Regra 8.14). Ele
 * só existe no toque — nas outras entradas a primeira pergunta já decide.
 */
export type DecisaoDeArraste = 'mover-o-quadro' | 'selecionar' | 'esperar-o-dedo';

export interface ArrasteNoVazio {
  /** `PointerEvent.pointerType` do `pointerdown` que iniciou o arraste. */
  tipoDePonteiro: TipoDePonteiro | undefined;
  /** `Shift` no instante do `pointerdown`. */
  comShift: boolean;
  /**
   * O ajuste `edgelessSelectDragPan`. `undefined` = o sinal de configuração ainda não emitiu
   * (ele nasce `{}` em `createSignalFromObservable`), e NÃO significa desligado.
   */
  ajuste: boolean | undefined;
}

export interface MovimentoDoDedo {
  /** Distância, em pixels de tela, entre onde o dedo pousou e onde ele está agora. */
  deslocamento: number;
  /**
   * Milissegundos entre o `pointerdown` e ESTE movimento. Os dois carimbos saem do mesmo
   * `PointerEvent.timeStamp`, que é `DOMHighResTimeStamp` — mesma base do `performance.now()`,
   * sem depender de relógio de parede e sem drift entre as duas leituras.
   */
  msDesdeOToque: number;
}

/**
 * A folga, em pixels de tela, dentro da qual o dedo ainda não "andou".
 *
 * 10px é a convenção das duas plataformas que importam aqui (o *touch slop* do Android é 8dp;
 * o do iOS fica na casa dos 10pt) e é o número que separa tremor de intenção. Abaixo dele
 * ninguém decide nada — que é o ponto.
 *
 * ⚠️ Ele é MAIOR que os 2px do `isFarEnough` do framework de propósito. Se um dia ficar menor
 * ou igual, a espera deixa de existir: a decisão volta a acontecer no primeiro movimento e o
 * tremor volta a decidir. Há teste guardando essa relação.
 */
export const FOLGA_DO_DEDO = 10;

/**
 * Quanto tempo o dedo precisa ficar parado para que andar signifique SELECIONAR.
 *
 * 500ms é o toque longo das duas plataformas (é o valor do `contextmenu` por toque nos
 * navegadores, e o do long press do Android). Escolher o número da plataforma em vez de um
 * nosso é o que faz o gesto parecer nativo a quem nunca leu esta linha.
 */
export const LIMIAR_DE_ESPERA_MS = 500;

/**
 * Entradas de manipulação DIRETA em que o gesto NÃO muda — o ponteiro toca a superfície onde a
 * coisa está desenhada, e continua desenhando a caixa de seleção.
 *
 * Sobrou só a **caneta**. O dedo saiu daqui no card 1864291811674032036 e passou a ter critério
 * próprio (`decidirQuandoODedoAnda`); a caneta ficou porque o operador falou de dedo — *"o
 * touch com um dedo e arrasta"* —, e o que não foi pedido não se muda. Com a Pencil na mão o
 * gesto esperado é marcar, não arrastar o fundo.
 *
 * O trackpad, inclusive o do iPad, é apontamento INDIRETO e chega como `mouse`. É por isso que
 * o caso que motivou o card anterior cai do outro lado desta lista.
 */
const ENTRADAS_DIRETAS: readonly string[] = ['pen'];

/**
 * Primeira pergunta, feita no `dragStart`: o que já dá para decidir agora?
 *
 * Devolve `selecionar` em toda porta de saída — o comportamento antigo é o destino seguro.
 * Devolve `esperar-o-dedo` só no toque, e aí quem decide é `decidirQuandoODedoAnda`.
 */
export function decidirNoInicioDoArraste({
  tipoDePonteiro,
  comShift,
  ajuste,
}: ArrasteNoVazio): DecisaoDeArraste {
  // O ajuste desligado é a única resposta que o operador dá explicitamente. Vem primeiro, e
  // vale para TODAS as entradas — inclusive o dedo, senão desligar a função no ajuste deixaria
  // o toque panejando e o mouse não.
  if (ajuste === false) return 'selecionar';

  // `Shift` é a saída para a seleção múltipla, que sem ela deixaria de existir no mouse.
  // É o desenho do Miro ("hold down the Shift key before you click and drag"), e é o que
  // torna isto uma troca de gesto em vez de uma função a menos.
  if (comShift) return 'selecionar';

  // Ponteiro que não se conhece NÃO paneja: na dúvida, o comportamento de hoje.
  if (tipoDePonteiro === undefined) return 'selecionar';

  if (ENTRADAS_DIRETAS.includes(tipoDePonteiro)) return 'selecionar';

  // O dedo é o único que não se decide agora — falta o tempo, que ainda não passou.
  if (tipoDePonteiro === 'touch') return 'esperar-o-dedo';

  return 'mover-o-quadro';
}

/**
 * Segunda pergunta, feita a cada movimento ENQUANTO a resposta for `esperar-o-dedo`.
 *
 * É aqui que mora o comportamento do Miro que o card pede: andar logo move o quadro; ficar
 * parado e só então andar seleciona.
 */
export function decidirQuandoODedoAnda({
  deslocamento,
  msDesdeOToque,
}: MovimentoDoDedo): DecisaoDeArraste {
  // Dentro da folga o dedo não andou — andou o tremor. Ninguém decide, nada acontece.
  if (!(deslocamento >= FOLGA_DO_DEDO)) return 'esperar-o-dedo';

  // O dedo ficou parado e só agora andou: é a troca de função que o card descreve.
  if (msDesdeOToque >= LIMIAR_DE_ESPERA_MS) return 'selecionar';

  return 'mover-o-quadro';
}
